import React from "react";

export default function ComputeGrowth(): JSX.Element {
  // Set dimensions and margins
  const width = 1000;
  const height = 700;
  const margin = { top: 40, right: 40, bottom: 60, left: 100 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  // Month labels for x-axis spanning from Dec 2023 to Dec 2027
  const months: string[] = [
    "2023-12", 
    "2024-06", "2024-12", 
    "2025-06", "2025-12", 
    "2026-06", "2026-12", 
    "2027-06", "2027-12"
  ];
  
  // Function to generate exponential growth curve - matches Python implementation
  const expGrowth = (startVal: number, growthRate: number, periods: number): number[] => {
    return Array.from({ length: periods }, (_, i) => startVal * Math.pow(growthRate, i/2));
  };
  
  // Generate data points for global compute
  const globalStart = 4e27;  // Starting value in Dec 2023
  const globalEnd = 1e29;    // End value in Dec 2027
  const globalGrowthRate = Math.pow(globalEnd/globalStart, 1/4);  // Calculate annual growth rate
  const globalCompute = expGrowth(globalStart, globalGrowthRate, months.length);
  
  // Market share by year: 2.5% (2023), 7% (2024), 12% (2025), 16% (2026), 19% (2027)
  const companyShares = [0.025, 0.07, 0.12, 0.16, 0.19];
  const companyCompute = [];
  
  // Get global compute values at each December
  const decIndices = [0];  // Start with Dec 2023
  for (let year = 1; year < 5; year++) {  // Add Dec 2024, 2025, 2026, 2027
    decIndices.push(decIndices[0] + year * 2);  // Adjusted for semiannual data points
  }
  
  // Create month-by-month data with interpolated shares
  for (let i = 0; i < months.length; i++) {
    // Find which year segment we're in
    const yearSegment = Math.min(4, Math.floor(i / 2));  // Adjusted for semiannual data points
    
    let share;
    if (i % 2 === 0) {  // December values use exact percentages
      share = companyShares[yearSegment];
    } else {
      // Interpolate between December values
      const startShare = companyShares[yearSegment];
      const endShare = companyShares[Math.min(4, yearSegment + 1)];
      const progress = 0.5;  // Halfway point for June
      share = startShare + progress * (endShare - startShare);
    }
    
    companyCompute.push(globalCompute[i] * share);
  }
  
  // Formatted month labels for display
  const displayMonths: string[] = months.map(m => {
    const [year, month] = m.split("-");
    const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthNames[parseInt(month)]} ${year}`;
  });

  // Maximum and minimum values for scaling
  const minValue: number = 9e25; // Minimum y-value (log scale)
  const maxValue: number = 1.5e29; // Maximum y-value for plotting

  // Scales: x based on index, y based on logarithmic scale
  const xScale = (i: number): number => margin.left + (i / (months.length - 1)) * innerWidth;
  
  // Logarithmic y-scale
  const yScale = (value: number): number => {
    if (value <= 0) return margin.top + innerHeight; // Handle zero or negative values
    const logMin = Math.log10(minValue) - 0.1;
    const logMax = Math.log10(maxValue) + 0.01;
    const logValue = Math.log10(value);
    return margin.top + innerHeight - ((logValue - logMin) / (logMax - logMin)) * innerHeight;
  };

  // Generate x-values for all data points
  const xValues: number[] = months.map((_, i) => xScale(i));

  // Generate y-axis ticks (log scale)
  const yTicks: number[] = [1e26, 1e27, 1e28, 1e29];
  
  // Choose select x-axis tick indices for clarity
  const xTickIndices: number[] = [0, 2, 4, 6, 8];
  
  // Line generation helper
  const generateLinePath = (data: number[]): string => {
    const points: {x: number, y: number}[] = data
      .map((d, i) => ({ x: xScale(i), y: yScale(d) }));
    
    if (points.length === 0) return "";
    
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
      .join(" ");
  };

  // Area generation helper
  const generateAreaPath = (data: number[]): string => {
    const points: {x: number, y: number}[] = data
      .map((d, i) => ({ x: xScale(i), y: yScale(d) }));
    
    if (points.length === 0) return "";
    
    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
      .join(" ");
    
    // Use margin.top + innerHeight instead of yScale(minValue) to ensure it goes to the x-axis
    return `${linePath} L ${xScale(data.length - 1)} ${margin.top + innerHeight} L ${xScale(0)} ${margin.top + innerHeight} Z`;
  };

  // Create paths for each data series
  const globalAIPath: string = generateLinePath(globalCompute);
  const globalAIArea: string = generateAreaPath(globalCompute);
  const leadingCompanyPath: string = generateLinePath(companyCompute);
  const leadingCompanyArea: string = generateAreaPath(companyCompute);

  // Define colors for each series - updated to match Training Run Curves
  const colors = {
    globalAI: "rgba(0, 100, 0, 1)",        // Brighter green for Global AI (like Agent-2)
    globalAIFill: "rgba(0, 140, 0, 0.1)",    // Light green fill
    leadingCompany: "rgba(45, 45, 45, 1)",   // Darker grey for Leading Company (like Agent-0)
    leadingCompanyFill: "rgba(45, 45, 45, 0.1)", // Light grey fill
    grid: "#cccccc",                         // Light gray for grid
    text: "#333333"                          // Dark gray for text
  };

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={margin.top-10}
          fontSize="32"
          fontFamily="monospace"
          textAnchor="left"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          Global AI Compute with Leading Company Share
        </text>

        {/* Y-axis label */}
        <text
          transform={`rotate(-90, ${margin.left - 60}, ${margin.top + innerHeight/2})`}
          x={margin.left - 60}
          y={margin.top + innerHeight/2.1}
          textAnchor="middle"
          className="text-[20px] font-mono"
          fill={colors.text}
        >
          H100 equivalents (log scale)
        </text>

        {/* Grid lines - vertical */}
        {xValues.map((x, i) => (
          <line
            key={`vgrid-${i}`}
            x1={x}
            y1={margin.top}
            x2={x}
            y2={margin.top + innerHeight}
            stroke={colors.grid}
            strokeWidth="1"
            opacity="0.3"
          />
        ))}

        {/* Grid lines - horizontal (log scale) */}
        {yTicks.map((tick, i) => (
          <line
            key={`hgrid-${i}`}
            x1={margin.left}
            y1={yScale(tick)}
            x2={margin.left + innerWidth}
            y2={yScale(tick)}
            stroke={colors.grid}
            strokeWidth="1"
            opacity="0.3"
          />
        ))}

        {/* Area fills for data series */}
        <path
          d={globalAIArea}
          fill={colors.globalAIFill}
          opacity="0.8"
        />
        <path
          d={leadingCompanyArea}
          fill={colors.leadingCompanyFill}
          opacity="0.8"
        />

        {/* Global AI line */}
        <path
          d={globalAIPath}
          fill="none"
          stroke={colors.globalAI}
          strokeWidth="4"
        />
        {globalCompute.map((d, i) => (
          <circle
            key={`global-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.globalAI}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Leading Company line */}
        <path
          d={leadingCompanyPath}
          fill="none"
          stroke={colors.leadingCompany}
          strokeWidth="4"
        />
        {companyCompute.map((d, i) => (
          <circle
            key={`company-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.leadingCompany}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Growth rate annotations */}
        <text
          x={xScale(3) + 20}
          y={yScale((globalCompute[2] + globalCompute[4]) / 2) - 15}
          textAnchor="start"
          className="text-[16px] font-mono"
          fill={colors.globalAI}
          transform={`rotate(-16, ${xScale(3) + 20}, ${yScale((globalCompute[2] + globalCompute[4]) / 2) - 15})`}
        >
          2.25x / year
        </text>

        <text
          x={xScale(3) + 50}
          y={yScale((companyCompute[2] + companyCompute[4]) / 2) - 15}
          textAnchor="start"
          className="text-[16px] font-mono"
          fill={colors.leadingCompany}
          transform={`rotate(-21, ${xScale(3) + 20}, ${yScale((companyCompute[2] + companyCompute[4]) / 2) + 15})`}
        >
          3.4x / year
        </text>

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => (
          <g key={`xtick-${index}`}>
            <line
              x1={xValues[index]}
              y1={margin.top + innerHeight}
              x2={xValues[index]}
              y2={margin.top + innerHeight + 5}
              stroke={colors.text}
              strokeWidth="1.5"
            />
            <text
              x={xValues[index]}
              y={margin.top + innerHeight + 25}
              textAnchor="middle"
              className="text-[16px] font-mono"
              fill={colors.text}
            >
              {`Dec ${months[index].split("-")[0]}`}
            </text>
          </g>
        ))}

        {/* Y-axis ticks and labels */}
        {yTicks.map((tick) => (
          <g key={`ytick-${tick}`}>
            <line
              x1={margin.left - 5}
              y1={yScale(tick)}
              x2={margin.left}
              y2={yScale(tick)}
              stroke={colors.text}
              strokeWidth="1.5"
            />
            <text
              x={margin.left - 10}
              y={yScale(tick) + 5}
              textAnchor="end"
              className="text-[16px] font-mono"
              fill={colors.text}
            >
              {tick === 1e26 ? "100K" : 
               tick === 1e27 ? "1M" : 
               tick === 1e28 ? "10M" : 
               tick === 1e29 ? "100M" : `${tick}`}
            </text>
          </g>
        ))}

        {/* Legend */}
        <text
          x={margin.left+20}
          y={margin.bottom+365}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.globalAI}
        >
          Global AI Compute
        </text>

        <text
          x={margin.left+650}
          y={margin.bottom+500}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.leadingCompany}
        >
          Leading US Company
        </text>
      </svg>
    </div>
  );
}